<template>
  <div class="mc-portal">
    <PortalTopBar :role="barRole" />

    <div class="pt-container">
      <div class="pt-head">
        <div>
          <div class="pt-kicker">Your account</div>
          <h1 class="pt-title">My settings <span v-if="isCEO" class="ceo">CEO</span></h1>
          <p class="pt-sub">Your profile, your progress and your sign-in.</p>
        </div>
      </div>

      <div class="layout">
        <!-- Sidebar -->
        <aside class="side">
          <section class="pt-card me">
            <div class="avatar-wrap">
              <span class="avatar" :style="photoURL ? { backgroundImage: `url(${photoURL})` } : {}">{{ photoURL ? "" : initials(displayName, email) }}</span>
              <button type="button" class="avatar-edit" :disabled="uploadingPhoto" aria-label="Change photo" title="Change photo" @click="$refs.photo.click()">
                <SiteIcon :name="uploadingPhoto ? 'refresh' : 'pencil'" :size="14" />
              </button>
              <input ref="photo" type="file" accept="image/*" class="hidden" @change="onPhoto" />
            </div>
            <b class="me-name">{{ displayName || "Your name" }}<span v-if="isCEO" class="ceo small">CEO</span></b>
            <span class="pt-hint">{{ email }}</span>
          </section>

          <!-- Points -->
          <section class="pt-card score">
            <div class="score-top">
              <span class="score-icon"><SiteIcon name="trophy" :size="20" /></span>
              <div>
                <div class="pt-kpi-label">Your points</div>
                <div class="score-value">{{ stats.points.toLocaleString() }}</div>
              </div>
              <div v-if="stats.streakDays > 1" class="streak" :title="`${stats.streakDays} days in a row`"><SiteIcon name="flame" :size="15" /> {{ stats.streakDays }}</div>
            </div>
            <div class="today">+{{ todayPoints }} today</div>
            <div class="bar"><span :style="{ width: badgeProgress + '%' }"></span></div>
            <div class="pt-hint">
              <template v-if="nextBadge">{{ nextBadge.min - stats.points }} points to <b>{{ nextBadge.name }}</b></template>
              <template v-else>Every badge earned. Nice work.</template>
            </div>
            <div v-if="earned.length" class="badges">
              <span v-for="b in earned" :key="b.name" class="pt-badge green"><SiteIcon name="check" :size="12" /> {{ b.name }}</span>
            </div>
          </section>

          <!-- Activity -->
          <section class="pt-card activity">
            <div class="act-head"><SiteIcon name="sparkline" :size="17" /> Last 14 days</div>
            <div v-if="!activityLoaded"><div class="pt-skeleton slim"></div></div>
            <p v-else-if="activityError" class="pt-hint">{{ activityError }}</p>
            <p v-else-if="!totalActivity" class="pt-hint">Log a call, email or meeting on a lead and it shows up here.</p>
            <template v-else>
              <div class="bars" role="img" :aria-label="`${totalActivity} calls, emails and meetings in the last 14 days`">
                <span v-for="(n, i) in activityDays" :key="i" class="day" :title="`${dayLabels[i]}: ${n}`">
                  <span class="day-bar" :style="{ height: Math.max(4, (n / maxDay) * 56) + 'px' }" :class="{ zero: !n }"></span>
                </span>
              </div>
              <div class="act-stats">
                <div><b>{{ counts.call }}</b><span>Calls</span></div>
                <div><b>{{ counts.email }}</b><span>Emails</span></div>
                <div><b>{{ counts.meeting }}</b><span>Meetings</span></div>
              </div>
            </template>
          </section>
        </aside>

        <!-- Main -->
        <section class="pt-card main">
          <div class="pt-tabs tabs" role="tablist">
            <button type="button" class="pt-tab" :class="{ active: tab === 'profile' }" @click="go('profile')"><SiteIcon name="users" :size="16" /> Profile</button>
            <button type="button" class="pt-tab" :class="{ active: tab === 'security' }" @click="go('security')"><SiteIcon name="lock" :size="16" /> Sign-in &amp; password</button>
          </div>

          <!-- Profile -->
          <form v-if="tab === 'profile'" class="pt-stack form" @submit.prevent="saveProfile">
            <div class="pt-grid-2">
              <label class="pt-field"><span class="pt-label">First name</span><input v-model.trim="profile.firstName" class="pt-input" autocomplete="given-name" /></label>
              <label class="pt-field"><span class="pt-label">Last name</span><input v-model.trim="profile.lastName" class="pt-input" autocomplete="family-name" /></label>
              <label class="pt-field"><span class="pt-label">Job title</span><input v-model.trim="profile.title" class="pt-input" placeholder="Account Executive" /></label>
              <label class="pt-field"><span class="pt-label">Phone</span><input v-model.trim="profile.phone" class="pt-input" type="tel" autocomplete="tel" placeholder="(339) 555-0100" /></label>
            </div>
            <label class="pt-field">
              <span class="pt-label">Email</span>
              <input :value="email" class="pt-input" disabled />
              <span class="pt-hint">Your sign-in email. Ask an admin if it needs to change.</span>
            </label>
            <label class="pt-field">
              <span class="pt-label">Mailing address <em>(for payment checks and tax forms)</em></span>
              <textarea v-model="profile.address" class="pt-textarea" rows="3" autocomplete="street-address" placeholder="Street, town, state, ZIP"></textarea>
            </label>
            <p class="pt-hint">Your name is what the team sees on the leaderboard and on the leads you own.</p>
            <div class="foot">
              <span v-if="profileDirty" class="pt-hint">Unsaved changes</span>
              <button type="submit" class="pt-btn pt-btn-primary" :disabled="savingProfile || !profileDirty">{{ savingProfile ? "Saving…" : "Save profile" }}</button>
            </div>
          </form>

          <!-- Security -->
          <div v-if="tab === 'security'" class="pt-stack form">
            <dl class="pt-dl">
              <div class="pt-dl-row"><dt>Signed in with</dt><dd>{{ hasPassword ? "Email and password" : "Google" }}</dd></div>
              <div class="pt-dl-row"><dt>Last sign-in</dt><dd>{{ lastSignIn || "—" }}</dd></div>
            </dl>

            <template v-if="hasPassword">
              <div class="pt-label sep">Change password</div>
              <form class="pt-stack" @submit.prevent="changePassword">
                <label class="pt-field">
                  <span class="pt-label">Current password</span>
                  <span class="pw">
                    <input v-model="pw.current" class="pt-input" :type="showPw ? 'text' : 'password'" autocomplete="current-password" />
                  </span>
                </label>
                <label class="pt-field">
                  <span class="pt-label">New password</span>
                  <span class="pw">
                    <input v-model="pw.next" class="pt-input" :type="showPw ? 'text' : 'password'" autocomplete="new-password" />
                    <button type="button" class="pw-toggle" :aria-label="showPw ? 'Hide passwords' : 'Show passwords'" @click="showPw = !showPw"><SiteIcon :name="showPw ? 'eyeOff' : 'eye'" :size="18" /></button>
                  </span>
                  <span v-if="pw.next" class="strength">
                    <span class="strength-bar"><span :class="strength.tone" :style="{ width: strength.percent + '%' }"></span></span>
                    <span class="strength-label" :class="strength.tone">{{ strength.label }}</span>
                  </span>
                </label>
                <label class="pt-field">
                  <span class="pt-label">Confirm new password</span>
                  <input v-model="pw.confirm" class="pt-input" :type="showPw ? 'text' : 'password'" autocomplete="new-password" />
                  <span v-if="pw.confirm && pw.confirm !== pw.next" class="pt-hint bad">Doesn't match the new password yet.</span>
                </label>
                <div v-if="pwError" class="pt-notice error"><SiteIcon name="alert" :size="17" /><span>{{ pwError }}</span></div>
                <div class="foot">
                  <button type="submit" class="pt-btn pt-btn-ink" :disabled="changingPw">{{ changingPw ? "Changing…" : "Change password" }}</button>
                </div>
              </form>

              <div class="pt-label sep">Forgot your current password?</div>
              <p class="lead">We'll email a reset link to <b>{{ email }}</b>. It works for one hour.</p>
              <div>
                <button type="button" class="pt-btn pt-btn-outline" :disabled="resetting || cooldown > 0" @click="sendReset">
                  <SiteIcon name="send" :size="16" />
                  <template v-if="resetting">Sending…</template>
                  <template v-else-if="cooldown > 0">Sent. Resend in {{ cooldown }}s</template>
                  <template v-else>Email me a reset link</template>
                </button>
              </div>
              <div v-if="resetError" class="pt-notice error"><SiteIcon name="alert" :size="17" /><span>{{ resetError }}</span></div>
            </template>

            <div v-else class="pt-notice info">
              <SiteIcon name="info" :size="17" />
              <span>You sign in with Google, so there's no password to change here. Manage it in your Google account.</span>
            </div>

            <div class="sep-block">
              <button type="button" class="pt-btn pt-btn-outline" @click="signOut"><SiteIcon name="logout" :size="16" /> Sign out of this device</button>
            </div>
          </div>
        </section>
      </div>
    </div>

    <div v-if="toastMsg" class="pt-toast" role="status">
      <span>{{ toastMsg }}</span>
      <button type="button" @click="toastMsg = ''">Close</button>
    </div>
  </div>
</template>

<script>
import "@/styles/portal.css";
import { auth, db, storage, functions } from "@/firebase";
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { doc, getDoc, setDoc, onSnapshot, collectionGroup, query, where, getDocs, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import PortalTopBar from "@/components/PortalTopBar.vue";
import SiteIcon from "@/components/SiteIcon.vue";
import { BADGES, dayKey } from "@/config/staffBadges";
import { initials, formatDateTime, toDate } from "@/config/format";

const emptyProfile = () => ({ firstName: "", lastName: "", title: "", phone: "", address: "" });

export default {
  name: "StaffSettings",
  components: { PortalTopBar, SiteIcon },
  data() {
    return {
      tab: this.$route.query.tab === "security" ? "security" : "profile",
      barRole: "staff",
      userType: "",
      email: "",
      photoURL: "",
      uploadingPhoto: false,
      profile: emptyProfile(),
      savedProfile: JSON.stringify(emptyProfile()),
      savingProfile: false,
      stats: { points: 0, todayPoints: 0, dayKey: "", streakDays: 0 },
      activityLoaded: false,
      activityError: "",
      activityDays: Array(14).fill(0),
      counts: { call: 0, email: 0, meeting: 0 },
      hasPassword: true,
      lastSignIn: "",
      pw: { current: "", next: "", confirm: "" },
      showPw: false,
      changingPw: false,
      pwError: "",
      resetting: false,
      resetError: "",
      cooldown: 0,
      toastMsg: "",
    };
  },
  computed: {
    isCEO() {
      return String(this.userType).toLowerCase() === "ceo";
    },
    displayName() {
      return `${this.profile.firstName} ${this.profile.lastName}`.trim();
    },
    profileDirty() {
      return JSON.stringify(this.profile) !== this.savedProfile;
    },
    todayPoints() {
      return this.stats.dayKey === dayKey() ? this.stats.todayPoints || 0 : 0;
    },
    earned() {
      return BADGES.filter((b) => this.stats.points >= b.min);
    },
    nextBadge() {
      return BADGES.find((b) => this.stats.points < b.min) || null;
    },
    badgeProgress() {
      if (!this.nextBadge) return 100;
      const prev = [...BADGES].reverse().find((b) => this.stats.points >= b.min);
      const from = prev ? prev.min : 0;
      return Math.round(((this.stats.points - from) / (this.nextBadge.min - from)) * 100);
    },
    totalActivity() {
      return this.counts.call + this.counts.email + this.counts.meeting;
    },
    maxDay() {
      return Math.max(1, ...this.activityDays);
    },
    dayLabels() {
      return Array.from({ length: 14 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (13 - i));
        return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
      });
    },
    strength() {
      const p = this.pw.next || "";
      let s = 0;
      if (p.length >= 8) s += 25;
      if (p.length >= 12) s += 25;
      if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s += 25;
      if (/[0-9]/.test(p)) s += 12.5;
      if (/[^a-zA-Z0-9]/.test(p)) s += 12.5;
      if (s >= 75) return { percent: s, label: "Strong", tone: "good" };
      if (s >= 50) return { percent: s, label: "Okay", tone: "ok" };
      return { percent: Math.max(s, 8), label: "Weak", tone: "weak" };
    },
  },
  watch: {
    "$route.query.tab"(t) {
      this.tab = t === "security" ? "security" : "profile";
    },
  },
  mounted() {
    this._unsubAuth = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        this.$router.push("/login");
        return;
      }
      this.email = u.email || "";
      this.photoURL = u.photoURL || "";
      this.hasPassword = (u.providerData || []).some((p) => p.providerId === "password") || !(u.providerData || []).length;
      this.lastSignIn = u.metadata?.lastSignInTime ? formatDateTime(new Date(u.metadata.lastSignInTime)) : "";
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        const d = snap.exists() ? snap.data() : {};
        // Older records only have displayName; split it so the form has first and last.
        const [first, ...rest] = String(d.displayName || u.displayName || "").trim().split(/\s+/);
        this.profile = {
          firstName: d.firstName || first || "",
          lastName: d.lastName || rest.join(" ") || "",
          title: d.title || "",
          phone: d.phone || "",
          address: d.address || "",
        };
        this.savedProfile = JSON.stringify(this.profile);
        this.photoURL = d.profilePhotoURL || this.photoURL;
        this.userType = d.type || "";
        this.barRole = d.role === "Admin" ? "admin" : "staff";
      } catch (e) {
        console.error("Couldn't load your profile:", e);
      }

      // Points: live, same user_stats doc the Leads page writes to.
      this._unsubStats = onSnapshot(
        doc(db, "user_stats", u.uid),
        (s) => {
          const d = s.exists() ? s.data() : {};
          this.stats = { points: d.points || 0, todayPoints: d.todayPoints || 0, dayKey: d.dayKey || "", streakDays: d.streakDays || 0 };
        },
        (e) => console.error("Points:", e)
      );

      this.loadActivity(u.uid);
    });
  },
  beforeDestroy() {
    if (this._unsubAuth) this._unsubAuth();
    if (this._unsubStats) this._unsubStats();
    clearInterval(this._cool);
  },
  methods: {
    initials,
    go(t) {
      this.tab = t;
      if (this.$route.query.tab !== t) this.$router.replace({ query: { ...this.$route.query, tab: t } }).catch(() => {});
    },
    toast(m) {
      this.toastMsg = m;
      clearTimeout(this._t);
      this._t = setTimeout(() => (this.toastMsg = ""), 3500);
    },
    // Calls, emails and meetings you logged on any lead (leads/{id}/interactions where by == you).
    async loadActivity(uid) {
      try {
        const snap = await getDocs(query(collectionGroup(db, "interactions"), where("by", "==", uid)));
        const counts = { call: 0, email: 0, meeting: 0 };
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        start.setDate(start.getDate() - 13);
        const days = Array(14).fill(0);
        snap.docs.forEach((ds) => {
          const d = ds.data();
          const at = toDate(d.at);
          if (!at || at < start) return;
          if (counts[d.type] !== undefined) counts[d.type] += 1;
          const idx = Math.floor((at - start) / 86400000);
          if (idx >= 0 && idx < 14) days[idx] += 1;
        });
        this.counts = counts;
        this.activityDays = days;
      } catch (e) {
        console.error("Activity:", e);
        // A collection-group query needs its index the first time; the console error has a link to create it.
        this.activityError = "Activity couldn't load yet. An admin may need to create a Firestore index (see the browser console).";
      } finally {
        this.activityLoaded = true;
      }
    },
    async onPhoto(e) {
      const f = e.target.files && e.target.files[0];
      if (!f) return;
      if (f.size > 5 * 1024 * 1024) {
        this.toast("That photo is over 5 MB. Please choose a smaller one.");
        e.target.value = "";
        return;
      }
      this.uploadingPhoto = true;
      try {
        const u = auth.currentUser;
        const r = storageRef(storage, `avatars/${u.uid}/${Date.now()}_${f.name.replace(/[^\w.-]+/g, "_")}`);
        await uploadBytes(r, f);
        const url = await getDownloadURL(r);
        await updateProfile(u, { photoURL: url });
        await setDoc(doc(db, "users", u.uid), { profilePhotoURL: url }, { merge: true });
        this.photoURL = url;
        this.toast("Photo updated");
      } catch (err) {
        console.error(err);
        this.toast("Couldn't upload the photo");
      } finally {
        this.uploadingPhoto = false;
        e.target.value = "";
      }
    },
    async saveProfile() {
      if (!this.profile.firstName) {
        this.toast("Add your first name");
        return;
      }
      this.savingProfile = true;
      try {
        const u = auth.currentUser;
        await updateProfile(u, { displayName: this.displayName });
        await setDoc(
          doc(db, "users", u.uid),
          { ...this.profile, displayName: this.displayName, fullName: this.displayName, updatedAt: serverTimestamp() },
          { merge: true }
        );
        this.savedProfile = JSON.stringify(this.profile);
        this.toast("Profile saved");
      } catch (e) {
        console.error(e);
        this.toast("Couldn't save. Please try again.");
      } finally {
        this.savingProfile = false;
      }
    },
    async changePassword() {
      this.pwError = "";
      const { current, next, confirm } = this.pw;
      if (!current || !next) return (this.pwError = "Fill in your current and new password.");
      if (next.length < 8) return (this.pwError = "Use at least 8 characters.");
      if (next !== confirm) return (this.pwError = "The new passwords don't match.");
      if (next === current) return (this.pwError = "Pick a password you haven't used here before.");
      this.changingPw = true;
      try {
        const u = auth.currentUser;
        await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, current));
        await updatePassword(u, next);
        this.pw = { current: "", next: "", confirm: "" };
        this.toast("Password changed");
      } catch (e) {
        console.error(e);
        const code = e?.code || "";
        if (code.includes("wrong-password") || code.includes("invalid-credential")) this.pwError = "Your current password isn't right.";
        else if (code.includes("too-many-requests")) this.pwError = "Too many tries. Wait a few minutes, or use the reset link below.";
        else if (code.includes("weak-password")) this.pwError = "That password is too easy to guess. Try a longer one.";
        else this.pwError = "Couldn't change your password. Please try again.";
      } finally {
        this.changingPw = false;
      }
    },
    async sendReset() {
      this.resetting = true;
      this.resetError = "";
      try {
        await httpsCallable(functions, "sendPasswordResetEmail")({ email: this.email });
        this.toast("Reset link sent. Check your inbox.");
        this.cooldown = 60;
        clearInterval(this._cool);
        this._cool = setInterval(() => {
          this.cooldown -= 1;
          if (this.cooldown <= 0) clearInterval(this._cool);
        }, 1000);
      } catch (e) {
        console.error(e);
        this.resetError = "Couldn't send the email. Please try again.";
      } finally {
        this.resetting = false;
      }
    },
    async signOut() {
      if (this.profileDirty && !window.confirm("You have unsaved profile changes. Sign out anyway?")) return;
      await auth.signOut();
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
.ceo {
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--brass);
  color: #fff;
  font-family: var(--sans);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  vertical-align: middle;
}

.ceo.small {
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 10.5px;
}

.layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}

.side {
  display: grid;
  gap: 16px;
  position: sticky;
  top: 86px;
}

.side .pt-card + .pt-card {
  margin-top: 0;
}

.me {
  display: grid;
  justify-items: center;
  gap: 4px;
  text-align: center;
}

.avatar-wrap {
  position: relative;
  margin-bottom: 8px;
}

.avatar {
  width: 88px;
  height: 88px;
  display: grid;
  place-items: center;
  border: 3px double var(--brass);
  border-radius: 50%;
  background: var(--ink) center / cover no-repeat;
  color: var(--on-navy);
  font-family: var(--serif);
  font-size: 1.7rem;
  font-weight: 700;
}

.mc-portal .avatar-edit {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border: 2px solid var(--card);
  border-radius: 50%;
  background: var(--green);
  color: #fff;
  cursor: pointer;
}

.me-name {
  font-size: 1.05rem;
}

.score {
  display: grid;
  gap: 8px;
}

.score-top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.score-icon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--amber-tint);
  color: var(--brass);
}

.score-value {
  font-family: var(--serif);
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.streak {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
  padding: 4px 9px;
  border-radius: 999px;
  background: var(--red-tint);
  color: var(--red-ink);
  font-size: 13px;
  font-weight: 700;
}

.today {
  font-size: 13px;
  font-weight: 700;
  color: var(--green);
}

.bar {
  height: 7px;
  border-radius: 999px;
  background: var(--paper-2);
  overflow: hidden;
}

.bar span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--green), var(--brass));
}

.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.act-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 700;
  color: var(--ink);
}

.act-head .site-icon {
  color: var(--green);
}

.pt-skeleton.slim {
  height: 60px;
}

.bars {
  display: grid;
  grid-template-columns: repeat(14, minmax(0, 1fr));
  gap: 3px;
  align-items: end;
  height: 60px;
}

.day {
  display: flex;
  align-items: flex-end;
  height: 100%;
}

.day-bar {
  width: 100%;
  border-radius: 2px 2px 0 0;
  background: var(--green);
}

.day-bar.zero {
  background: var(--paper-2);
}

.act-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--rule);
  text-align: center;
}

.act-stats div {
  display: grid;
}

.act-stats b {
  font-family: var(--serif);
  font-size: 1.3rem;
}

.act-stats span {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}

.tabs {
  margin-bottom: 22px;
}

.form {
  max-width: 640px;
}

.foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.sep {
  margin-top: 8px;
  padding-top: 18px;
  border-top: 1px solid var(--rule);
}

.sep-block {
  margin-top: 8px;
  padding-top: 18px;
  border-top: 1px solid var(--rule);
}

.lead {
  margin-top: -6px;
  color: var(--ink-2);
  line-height: 1.6;
}

.pw {
  position: relative;
  display: block;
}

.mc-portal .pw .pt-input {
  padding-right: 44px;
}

.mc-portal .pw-toggle {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
}

.strength {
  display: flex;
  align-items: center;
  gap: 10px;
}

.strength-bar {
  flex: 1;
  height: 5px;
  border-radius: 999px;
  background: var(--paper-2);
  overflow: hidden;
}

.strength-bar span {
  display: block;
  height: 100%;
  transition: width 0.2s ease;
}

.strength-bar .weak { background: var(--red); }
.strength-bar .ok { background: var(--brass); }
.strength-bar .good { background: var(--green); }

.strength-label {
  min-width: 48px;
  font-size: 12px;
  font-weight: 700;
}

.strength-label.weak { color: var(--red-ink); }
.strength-label.ok { color: var(--amber-ink); }
.strength-label.good { color: var(--green-2); }

.bad {
  color: var(--red-ink);
}

.hidden {
  display: none;
}

@media (max-width: 960px) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .side {
    position: static;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }
}

@media (max-width: 520px) {
  .tabs {
    display: flex;
    width: 100%;
  }
  .tabs .pt-tab {
    flex: 1;
    justify-content: center;
    padding: 0 8px;
  }
  .foot .pt-btn {
    width: 100%;
  }
}
</style>